import { useRef, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from '../lib/gsapContext';

type Props = {
  to?: string;
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  variant?: 'primary' | 'ghost';
  className?: string;
};

export default function MagneticButton({
  to,
  href,
  onClick,
  children,
  variant = 'primary',
  className = '',
}: Props) {
  const ref = useRef<HTMLElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);
  const fillRef = useRef<HTMLSpanElement | null>(null);

  const MAX = 6;

  const onEnter = () => {
    if (variant !== 'primary') return;
    gsap.to(fillRef.current, { yPercent: 0, duration: 0.45, ease: 'power3.out' });
  };
  const onLeave = () => {
    gsap.to(innerRef.current, {
      x: 0,
      y: 0,
      duration: 0.6,
      ease: 'elastic.out(1, 0.5)',
    });
    if (variant === 'primary') {
      gsap.to(fillRef.current, {
        yPercent: 100,
        duration: 0.4,
        ease: 'power3.in',
      });
    }
  };
  const onMove = (e: MouseEvent<HTMLElement>) => {
    if (!ref.current || !innerRef.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) * 0.15;
    const y = (e.clientY - rect.top - rect.height / 2) * 0.15;
    const clamp = (v: number) => Math.max(-MAX, Math.min(MAX, v));
    gsap.to(innerRef.current, {
      x: clamp(x),
      y: clamp(y),
      duration: 0.4,
      ease: 'power3.out',
    });
  };

  const base =
    'group relative inline-flex items-center gap-3 overflow-hidden rounded-full px-7 py-3.5 font-sans text-[0.7rem] uppercase tracking-label';
  const styles =
    variant === 'primary'
      ? 'border border-gold/60 text-white'
      : 'border border-white/20 text-white/90 transition-colors hover:text-white';

  const content = (
    <>
      <span
        ref={fillRef}
        aria-hidden
        className="absolute inset-0 -z-0 bg-gold"
        style={{ transform: 'translateY(100%)' }}
      />
      <span
        ref={innerRef}
        className="relative z-10 inline-flex items-center gap-3 will-change-transform"
      >
        <span>{children}</span>
        <span>→</span>
      </span>
    </>
  );

  const handlers = {
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    onMouseMove: onMove,
    onClick,
  };

  if (to) {
    return (
      <Link
        to={to}
        ref={(el) => {
          ref.current = el;
        }}
        className={`${base} ${styles} ${className}`}
        {...handlers}
      >
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        ref={(el) => {
          ref.current = el;
        }}
        className={`${base} ${styles} ${className}`}
        {...handlers}
      >
        {content}
      </a>
    );
  }
  return (
    <button
      ref={(el) => {
        ref.current = el;
      }}
      className={`${base} ${styles} ${className}`}
      {...handlers}
    >
      {content}
    </button>
  );
}
